import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import { statusCategories } from "@Features/portal-settings";
import { t } from "@i18n";
import { convertDate } from "@Services/luxonHelper";
import { reviewStageMapper } from "./ReviewsConstants";
import type {
  CreateReviewResponse,
  GetReviewDataRequest,
  GetReviewDataResponse,
  SaveUserResponseForReviewRequest,
  SaveUserResponseForReviewResponse,
  UserPermissionsToMarkReviewRequest,
  UserPermissionsToMarkReviewResponse,
  SendNotificationForReviewRequest,
  SendNotificationForReviewResponse,
  FetchListOfStatusesAvailableResponse,
  FetchBulkTransitionStatusListResponse,
  ChangeResourceStatusInReviewRequest,
  ChangeResourceStatusInReviewResponse,
  NotifyUsersInLevelRequest,
  NotifyUsersInLevelResponse,
  NotifyUserInReviewRequest,
  NotifyUserInReviewResponse,
  GetFileStatusInAllReviewsRequest,
  GetFileStatusInAllReviewsResponse,
  GetReviewStatusForAllFileVersionsRequest,
  GetReviewStatusForAllFileVersionsResponse,
  InitiateReviewRequest,
  InitiateReviewResponse,
  AddResourcesToReviewResponse,
  RemoveResourcesFromReviewResponse,
  RollbackReviewRequest,
  RollbackReviewResponse,
  GetReviewsListForEntityRequest,
  GetReviewsListForEntityResponse,
  CanUserClearReviewLevelRequest,
  CanUserClearReviewLevelResponse,
  UpdateFilesInReviewRequest,
  UpdateFilesInReviewResponse,
  GetResourcesInReviewRequest,
  GetResourcesInReviewResponse,
  GetIssuesDetailsInReviewRequest,
  GetIssuesDetailsInReviewResponse,
  AddSignatureToResourcesResponse,
  AddReferencedFilesInReviewsRequest,
  AddReferencedFilesInReviewsResponse,
  UpdateReviewStatusRequest,
  UpdateReviewStatusResponse,
  ChecklistsInReviewsRequest,
  ChecklistsInReviewsResponse,
  GetChecklistQuestionCommentsRequest,
  GetChecklistQuestionCommentsResponse,
  GetChecklistQuestionHistoryRequest,
  GetChecklistQuestionHistoryResponse,
  ManageChecklistRequest,
  ManageChecklistResponse,
  ReviewDataResult,
  CreateReviewRequest,
  AddResourcesToReviewRequest,
  RemoveResourcesFromReviewRequest,
  FetchListOfStatusesAvailableRequest,
  FetchBulkTransitionStatusListRequest,
  AddSignatureToResourcesRequest,
} from "@ServerTypes/reviews/reviews.schema";
import type { GetCustomFieldsRequest, GetCustomFieldsResponse } from "@ServerTypes/issues/issues.schema";
import type { Exact } from "@Helpers/types/exact";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import { typedColumns } from "@CubeComponents/html/SlickGrid1";
import type {
  ColumnDefinition,
  GridRowBase,
  ToolbarItem,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";

/**
 * One resource descriptor exactly as `reviews.schema.ts` `resourceSchema` declares it
 * (resourceType plus the id field(s) that type selects). Sourced through
 * `fetchListOfStatusesAvailableRequest` because `resourceSchema` itself is module-private.
 *
 * This uses the exported `z.infer` alias, so the ids here are BRANDED `objectIdString`.
 *
 * An earlier note in this spot argued the opposite — that `z.input` was load-bearing because every
 * producer holds a plain `string`, and that `reviews.interfaces.ts` could not be re-branded. That is
 * no longer true and the note contradicted the code below it, so it has been replaced. What changed:
 * `reviews.interfaces.Resource` now declares its ids as `ObjectIdString` (and gained the `issueId` /
 * `estimateId` / `estimateVersionId` it was always given at runtime), the two grids brand their
 * `fileVersionId` / `issueId` params, and `ChangeFileStatusModal` asserts the brand at its two API
 * boundaries instead. The whole chain type-checks branded, with `zod` no longer imported by web at all.
 */
export type ReviewResourceDescriptor = FetchListOfStatusesAvailableRequest["value"]["params"]["resource"];

/** The id-bearing subset of a resource descriptor that {@link fileStatusHelper} reads off a grid row. */
export type ReviewResourceIds = Partial<
  Pick<
    ReviewResourceDescriptor,
    "resourceType" | "fileVersionId" | "estimateId" | "estimateVersionId" | "taskId" | "issueId"
  >
>;

/**
 * A resource descriptor as the client assembles it for `addResourcesToReview`, i.e. the
 * schema shape plus the client-only `name` that `ChecklistAssignmentModal` renders.
 * `resourceSchema` does not declare `name`, so Zod strips it before the service sees it —
 * see the note on {@link getResourceDetails}.
 */
export type ReviewResourceToAttach = ReviewResourceDescriptor & { name?: string };

/** One approval stage of a review, from `reviewDataResultSchema.approvalStages`. */
export type ReviewApprovalStage = NonNullable<ReviewDataResult["approvalStages"]>[number];

/** Windowed approval-stage view rendered by ReviewProgressBar / ReviewStageTransitionChart. */
export interface ApprovalStagesToShow {
  showStartLine: boolean;
  showEndLine: boolean;
  approvalStagesToShow: Array<ReviewApprovalStage & { rollbackTo: boolean }>;
  isReviewInitiated: boolean;
  willReviewClose: boolean;
}

/**
 * One row of the reviews list grid (`/reviewsService` → getReviewsList).
 * Hand-written because `reviewsServiceResponse.result` is `z.unknown()` — the server
 * publishes no shape for this grid. Field types are borrowed from
 * `reviewDataResultSchema` wherever the same sub-document is projected.
 */
export interface ReviewsGridRow extends GridRowBase {
  [key: string]: unknown;
  _id?: ObjectIdString;
  id?: ObjectIdString;
  serialId?: number;
  name?: string;
  description?: string | null;
  reviewStatus?: string;
  spaceId?: ObjectIdString;
  spaceName?: string;
  createdBy?: ReviewDataResult["createdBy"];
  createdDate?: number;
  finishedDate?: number | null;
  nextActionDueDate?: number | null;
  numberOfEntitiesAttached?: number;
  currentReviewLevel?: ReviewDataResult["currentReviewLevel"];
  reviewFlow?: ReviewDataResult["reviewFlow"];
  /** Denormalised project of the review's space; absent for reviews with no project. */
  projectData?: { _id: ObjectIdString; description?: string };
}

/** A cell template for the reviews grid: SlickGrid1 hands it the row (5th formatter arg). */
export type ReviewsGridCellTemplate = (row: ReviewsGridRow) => unknown;

/** `isReviewColumnType` is set by several grids and read by none; kept because it is emitted. */
export type ReviewsGridColumn = ColumnDefinition<ReviewsGridRow> & { isReviewColumnType?: boolean };

/** Row of the project-picker grid (TakeAction toolbar). No server shape: `includedProjectsUrl` is untyped. */
export interface ReviewSelectProjectRow extends GridRowBase {
  [key: string]: unknown;
  projectId?: string;
  description?: string;
}

/** Row of the reviewFlow-picker grid (Space settings -> ReviewFlows). No server shape published. */
export interface ReviewSelectReviewFlowRow extends GridRowBase {
  [key: string]: unknown;
  name?: string;
  description?: string;
  createdBy?: ReviewDataResult["createdBy"];
  createdDate?: number;
}

/** Custom-rendered toolbar entry, exactly as SlickGrid1's `ToolbarItem.template` declares it. */
export type ToolbarTemplate = NonNullable<ToolbarItem["template"]>;

/**
 * Accepts any cell/toolbar template function. Used for the Syncfusion-era column builders
 * below, whose callers pass templates with unrelated argument shapes.
 */
export type AnyGridTemplate = (...args: never[]) => unknown;

/**
 * Legacy Syncfusion column descriptor. `getFilesInReviewGridColumns`,
 * `getIssuesInReviewGridColumns` and `getResourcesActivityColumns` have no callers left
 * (the live grids build SlickGrid1 columns inline), so these keep the exact Syncfusion
 * prop names — `headerText`/`template`/`showInColumnChooser` are not SlickGrid1 props.
 */
export interface LegacyGridColumn {
  type?: string;
  field?: string;
  headerText?: string;
  width?: number | string;
  visible?: boolean;
  showInColumnChooser?: boolean;
  isPrimaryKey?: boolean;
  allowFiltering?: boolean;
  allowEditing?: boolean;
  allowSorting?: boolean;
  textAlign?: string;
  template?: AnyGridTemplate;
  editTemplate?: AnyGridTemplate;
}

export const fileStatusHelper = (args: ReviewResourceIds): ReviewResourceDescriptor => {
  const { resourceType } = args;
  const resource: ReviewResourceDescriptor = { resourceType };

  if (resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED") {
    const { fileVersionId } = args;
    resource.fileVersionId = fileVersionId;
  }

  if (resourceType === "RESOURCE_TYPE_ESTIMATE") {
    const { estimateId, estimateVersionId } = args;
    resource.estimateVersionId = estimateVersionId;
    resource.estimateId = estimateId;
  }

  if (resourceType === "RESOURCE_TYPE_TASK") {
    const { taskId } = args;
    resource.taskId = taskId;
  }

  if (resourceType === "RESOURCE_TYPE_ISSUE") {
    const { issueId } = args;
    resource.issueId = issueId;
  }

  return resource;
};

/**
 * One entry of the file-manager / entity-picker selection that {@link getResourceDetails} maps.
 * The pickers hand their rows over untyped — FilesInReview passes `Object.values(listOfFiles)`
 * (a `Record<string, unknown>`) and CreateReviewModal its `readonly unknown[]` `initialFiles` prop —
 * so every field is `unknown` here and is checked by {@link readString} / {@link readResourceId}
 * before it is used.
 */
export interface ReviewResourceSelection {
  resourceType?: unknown;
  /** File-manager selections carry the version id as `fileVersion`. */
  fileVersion?: unknown;
  /** Estimate selections carry the estimate id as `_id` and the version as `versionId`. */
  _id?: unknown;
  versionId?: unknown;
  issueId?: unknown;
  taskId?: unknown;
  name?: unknown;
}

/**
 * Every member of {@link ReviewResourceSelection} is optional and `unknown`, so establishing the
 * type needs nothing beyond "this row is an object" — which also drops the falsy entries the
 * previous `.filter(Boolean)` removed.
 */
const isResourceSelection = (resource: unknown): resource is ReviewResourceSelection =>
  typeof resource === "object" && resource !== null;

/** Keeps a picker field only when it really is a string. */
const readString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

/**
 * Re-validates a picker id against the 24-hex brand `resourceSchema` requires. The ids originate in
 * Mongo, but they reach this function as `unknown`, so they go through the boundary validator rather
 * than `toObjectIdString`, which brands without checking. An id that failed here would have been
 * rejected by the server's own `objectIdString` anyway.
 */
const readResourceId = (value: unknown): ObjectIdString | undefined => objectIdString.safeParse(value).data;

/**
 * NOTE: the FILE/EMBED branch emits `name`, which `resourceSchema` does not declare, so Zod
 * strips it and `addResourcesToReview`/`createReview` never see it. Verified benign: the
 * service reads only resourceType + the id fields + checklistAssignments. `name` exists
 * purely for `ChecklistAssignmentModal`, which renders `file.name` client-side.
 */
export const getResourceDetails = (resources: readonly unknown[]): ReviewResourceToAttach[] =>
  resources.filter(isResourceSelection).flatMap<ReviewResourceToAttach>((resource) => {
    const resourceType = readString(resource.resourceType);

    if (resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED") {
      return [{ fileVersionId: readResourceId(resource.fileVersion), resourceType, name: readString(resource.name) }];
    }

    if (resourceType === "RESOURCE_TYPE_MERGED_FILE") {
      return [{ fileVersionId: readResourceId(resource.fileVersion), resourceType }];
    }

    if (resourceType === "RESOURCE_TYPE_ESTIMATE") {
      return [
        {
          estimateId: readResourceId(resource._id),
          estimateVersionId: readResourceId(resource.versionId),
          resourceType,
        },
      ];
    }

    if (resourceType === "RESOURCE_TYPE_ISSUE") {
      return [{ issueId: readResourceId(resource.issueId), resourceType }];
    }

    if (resourceType === "RESOURCE_TYPE_TASKS") {
      return [{ taskId: readResourceId(resource.taskId), resourceType: "RESOURCE_TYPE_TASK" }];
    }

    return [];
  });

export const getApprovalStagesToShow = (
  approvalStages: ReviewApprovalStage[],
  isRollback: boolean,
  // Both levels are `useState<number | null>` at every call site (ReviewProgressBar,
  // ReviewStageTransitionChart) and every read below is truthiness-guarded before it is compared or
  // decremented, so the nullish forms are accepted rather than asserted away at the callers.
  skipReviewTillLevel: number | null | undefined,
  rollbackReviewToLevel: number | null | undefined,
): ApprovalStagesToShow => {
  let filteredStages = filterApprovalStages(approvalStages, isRollback, skipReviewTillLevel, rollbackReviewToLevel);

  const isReviewInitiated = filteredStages[0]?.clearanceStatus !== "pending";
  const willReviewClose = !isRollback && filteredStages.at(-1)?.clearanceStatus === "in_progress";

  const { startIndex, rollbackIndex } = getStartIndexAndRollbackIndex(
    filteredStages,
    isRollback,
    rollbackReviewToLevel,
  );

  const numberOfStagesToShow = isReviewInitiated ? 4 : 3;
  const endIndex = startIndex + numberOfStagesToShow;

  const showStartLine = startIndex >= 0;
  const showEndLine = endIndex < filteredStages.length;

  const approvalStagesToShow = filteredStages
    .slice(startIndex, endIndex)
    .map((stage: ReviewApprovalStage, idx: number) => ({
      ...stage,
      rollbackTo: rollbackIndex !== null && startIndex + idx === rollbackIndex,
    }));

  return {
    showStartLine,
    showEndLine,
    approvalStagesToShow,
    isReviewInitiated,
    willReviewClose,
  };
};

const filterApprovalStages = (
  approvalStages: ReviewApprovalStage[],
  isRollback: boolean,
  skipReviewTillLevel: number | null | undefined,
  rollbackReviewToLevel: number | null | undefined,
): ReviewApprovalStage[] => {
  let filteredStages = [...approvalStages];

  if (skipReviewTillLevel) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    filteredStages = filteredStages.filter(
      (stage, index) => index <= approvalStageInProgressIndex || index >= skipReviewTillLevel,
    );
  } else if (rollbackReviewToLevel && isRollback) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    const approvalStageApprovedIndex = filteredStages.findLastIndex(
      (stage: ReviewApprovalStage) => stage.serial === rollbackReviewToLevel,
    );
    filteredStages = filteredStages.filter(
      (stage, index) => index <= approvalStageApprovedIndex || index >= approvalStageInProgressIndex,
    );
  } else if (isRollback) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    const approvalStageApprovedIndex = filteredStages.findLastIndex(
      (stage: ReviewApprovalStage) => stage.clearanceStatus === "approved",
    );
    filteredStages = filteredStages.filter(
      (stage, index) => index < approvalStageApprovedIndex + 1 || index >= approvalStageInProgressIndex,
    );
  }

  return filteredStages;
};

const findApprovalStageInProgressIndex = (approvalStages: ReviewApprovalStage[]): number => {
  return approvalStages.findIndex((stage: ReviewApprovalStage) => stage.clearanceStatus === "in_progress");
};

const getStartIndexAndRollbackIndex = (
  approvalStages: ReviewApprovalStage[],
  isRollback: boolean,
  rollbackReviewToLevel: number | null | undefined,
): { startIndex: number; rollbackIndex: number | null } => {
  let startIndex = 0;
  let rollbackIndex: number | null = null;

  const approvalStageInProgressIndex = findApprovalStageInProgressIndex(approvalStages);
  const totalStages = approvalStages.length;

  if (approvalStageInProgressIndex === -1 && approvalStages[0]?.clearanceStatus !== "pending") {
    startIndex = Math.max(totalStages - 4, 0);
  }

  if (approvalStageInProgressIndex === -1 || approvalStageInProgressIndex === 0) {
    startIndex = 0;
  }

  if (approvalStageInProgressIndex > 0) {
    if (isRollback && rollbackReviewToLevel) {
      rollbackIndex = rollbackReviewToLevel - 1;
    } else if (isRollback) {
      rollbackIndex = approvalStageInProgressIndex - 1;
    }

    const diff = totalStages - approvalStageInProgressIndex;

    if (diff === 1) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 3);
    } else if (diff === 2) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 2);
    } else if (diff > 2) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 1);
    }
  }

  return { startIndex, rollbackIndex };
};

/**
 * `createReview`'s payload as a *caller* sends it. `createReviewRequest.resources` carries
 * `.optional().default([])`, so omitting it is legal on the wire — but `CreateReviewRequest` is
 * `z.infer`, i.e. the schema's OUTPUT type, where the default has already been applied and the
 * field is therefore required. Restating that one field as optional is the plain-TypeScript
 * equivalent of `z.input<typeof createReviewRequest>`, without depending on `zod` from the web
 * package (it is not in web's `package.json` — see the note on `fileSystemServer.schema.ts`).
 * `createReviewRequest` is a `z.object`, so `Omit` is safe here; it would silently flatten a
 * `z.looseObject`-derived type.
 */
type CreateReviewPayload = Omit<CreateReviewRequest, "resources"> & {
  resources?: CreateReviewRequest["resources"];
};

export const createNewReview = async (data: CreateReviewPayload) =>
  await axiosWrapper.post<CreateReviewResponse>(`${getBackendUrl()}/reviews/createReview`, data);

export const getReviewData = async (data: GetReviewDataRequest) =>
  await axiosWrapper.post<GetReviewDataResponse>(`${getBackendUrl()}/reviews/getReviewData`, data);

export const saveUserResponse = async (data: SaveUserResponseForReviewRequest) =>
  await axiosWrapper.post<SaveUserResponseForReviewResponse>(
    `${getBackendUrl()}/reviews/saveUserResponseForReview`,
    data,
  );

export const userPermissionsToMarkResponse = async (data: UserPermissionsToMarkReviewRequest) =>
  await axiosWrapper.post<UserPermissionsToMarkReviewResponse>(
    `${getBackendUrl()}/reviews/userPermissionsToMarkReview`,
    data,
  );

export const sendNotificationForReview = async (data: SendNotificationForReviewRequest) =>
  await axiosWrapper.post<SendNotificationForReviewResponse>(
    `${getBackendUrl()}/reviews/sendNotificationForReview`,
    data,
  );

export const fetchListOfStatusesAvailable = async (data: FetchListOfStatusesAvailableRequest) =>
  await axiosWrapper.post<FetchListOfStatusesAvailableResponse>(
    `${getBackendUrl()}/reviews/fetchListOfStatusesAvailable`,
    data,
  );

export const fetchBulkTransitionStatusList = async (data: FetchBulkTransitionStatusListRequest) =>
  await axiosWrapper.post<FetchBulkTransitionStatusListResponse>(
    `${getBackendUrl()}/reviews/fetchBulkTransitionStatusList`,
    data,
  );

export const changeResourceStatusInReview = async (data: ChangeResourceStatusInReviewRequest) =>
  await axiosWrapper.post<ChangeResourceStatusInReviewResponse>(
    `${getBackendUrl()}/reviews/changeResourceStatusInReview`,
    data,
  );

export const notifyUsersInLevel = async (data: NotifyUsersInLevelRequest) =>
  await axiosWrapper.post<NotifyUsersInLevelResponse>(`${getBackendUrl()}/reviews/notifyUsersInLevel`, data);

export const notifyUserInReview = async (data: NotifyUserInReviewRequest) =>
  await axiosWrapper.post<NotifyUserInReviewResponse>(`${getBackendUrl()}/reviews/notifyUserInReview`, data);

export const getFileStatusInReviews = async (data: GetFileStatusInAllReviewsRequest) =>
  await axiosWrapper.post<GetFileStatusInAllReviewsResponse>(
    `${getBackendUrl()}/reviews/getFileStatusInAllReviews`,
    data,
  );

export const getReviewStatusForAllFileVersions = async (data: GetReviewStatusForAllFileVersionsRequest) =>
  await axiosWrapper.post<GetReviewStatusForAllFileVersionsResponse>(
    `${getBackendUrl()}/reviews/getReviewStatusForAllFileVersions`,
    data,
  );

export const initiateReview = async (data: InitiateReviewRequest) =>
  await axiosWrapper.post<InitiateReviewResponse>(`${getBackendUrl()}/reviews/initiateReview`, data);

export const addResourcesToReview = async (data: AddResourcesToReviewRequest) =>
  await axiosWrapper.post<AddResourcesToReviewResponse>(`${getBackendUrl()}/reviews/addResourcesToReview`, data);

export const removeResourcesFromReview = async (data: RemoveResourcesFromReviewRequest) =>
  await axiosWrapper.post<RemoveResourcesFromReviewResponse>(
    `${getBackendUrl()}/reviews/removeResourcesFromReview`,
    data,
  );

export const getIssueCustomFields = async <T extends Exact<GetCustomFieldsRequest, T>>(data: T) =>
  await axiosWrapper.post<GetCustomFieldsResponse>(`${getBackendUrl()}/issues/getCustomFields`, data);

export const createStandaloneIssue = async <T extends Exact<GetCustomFieldsRequest, T>>(data: T) =>
  await axiosWrapper.post<GetCustomFieldsResponse>(`${getBackendUrl()}/issues/getCustomFields`, data);

export const rollbackReview = async (data: RollbackReviewRequest) =>
  await axiosWrapper.post<RollbackReviewResponse>(`${getBackendUrl()}/reviews/rollbackReview`, data);

export const getReviewsListForEntity = async (data: GetReviewsListForEntityRequest) =>
  await axiosWrapper.post<GetReviewsListForEntityResponse>(`${getBackendUrl()}/reviews/getReviewsListForEntity`, data);

export const canUserClearReviewLevel = async (data: CanUserClearReviewLevelRequest) =>
  await axiosWrapper.post<CanUserClearReviewLevelResponse>(`${getBackendUrl()}/reviews/canUserClearReviewLevel`, data);

export const updateFilesInReview = async (data: UpdateFilesInReviewRequest) =>
  await axiosWrapper.post<UpdateFilesInReviewResponse>(`${getBackendUrl()}/reviews/updateFilesInReview`, data);

export const getResourcesInReview = async (data: GetResourcesInReviewRequest) =>
  await axiosWrapper.post<GetResourcesInReviewResponse>(`${getBackendUrl()}/reviews/getResourcesInReviewService`, data);

export const getIssuesDetailsInReview = async (data: GetIssuesDetailsInReviewRequest) =>
  await axiosWrapper.post<GetIssuesDetailsInReviewResponse>(
    `${getBackendUrl()}/reviews/getIssuesDetailsInReviewService`,
    data,
  );

export const addSignatureToResources = async (data: AddSignatureToResourcesRequest) =>
  await axiosWrapper.post<AddSignatureToResourcesResponse>(`${getBackendUrl()}/reviews/addSignatureToResources`, data);

export const referencedFilesInReviews = async (
  data: AddReferencedFilesInReviewsRequest & { action?: "update" | "delete" },
) => {
  // Dispatch to the dedicated endpoints (split from the old action-dispatched /referencedFilesInReviews).
  const path =
    data.action === "delete" ? "/reviews/deleteReferencedFilesInReviews" : "/reviews/updateReferencedFilesInReviews";
  return await axiosWrapper.post<AddReferencedFilesInReviewsResponse>(`${getBackendUrl()}${path}`, data);
};

export const updateReviewStatus = async (data: UpdateReviewStatusRequest) => {
  return axiosWrapper.post<UpdateReviewStatusResponse>(`${getBackendUrl()}/reviews/updateReviewStatus`, data);
};

export const checklistsInReviews = async (data: ChecklistsInReviewsRequest) =>
  await axiosWrapper.post<ChecklistsInReviewsResponse>(`${getBackendUrl()}/reviews/checklistsInReviews`, data);

export const getChecklistQuestionComments = async (data: GetChecklistQuestionCommentsRequest) =>
  await axiosWrapper.post<GetChecklistQuestionCommentsResponse>(
    `${getBackendUrl()}/reviews/getChecklistQuestionComments`,
    data,
  );

export const getChecklistQuestionHistory = async (data: GetChecklistQuestionHistoryRequest) =>
  await axiosWrapper.post<GetChecklistQuestionHistoryResponse>(
    `${getBackendUrl()}/reviews/getChecklistQuestionHistory`,
    data,
  );

export const manageChecklist = async (data: ManageChecklistRequest) =>
  await axiosWrapper.post<ManageChecklistResponse>(`${getBackendUrl()}/reviews/manageChecklist`, data);

/**
 * `ReviewsGridRow.reviewStatus` is a free-form server string, so the `reviewStageMapper` lookup can
 * genuinely miss (an unmapped stage, or no stage at all on a row) — hence the runtime check rather
 * than an assertion that the string is one of the mapper's four keys.
 */
const isReviewStage = (reviewStatus: string): reviewStatus is keyof typeof reviewStageMapper =>
  reviewStatus in reviewStageMapper;

export const getReviewsGridColumns = ({
  creatorTemplate,
  createdDateTemplate,
  finishedDateTemplate,
  nextActionDueDateTemplate,
  waitingForTemplate,
  reviewSerialIdTemplate,
  reviewStageTemplate,
  reviewFlowTemplate,
  projectNameTemplate,
  navigateTemplate,
  showNavigateColumn = false,
}: {
  creatorTemplate: ReviewsGridCellTemplate;
  createdDateTemplate: ReviewsGridCellTemplate;
  finishedDateTemplate: ReviewsGridCellTemplate;
  nextActionDueDateTemplate: ReviewsGridCellTemplate;
  waitingForTemplate: ReviewsGridCellTemplate;
  reviewSerialIdTemplate: ReviewsGridCellTemplate;
  reviewStageTemplate: ReviewsGridCellTemplate;
  reviewFlowTemplate: ReviewsGridCellTemplate;
  projectNameTemplate: ReviewsGridCellTemplate;
  navigateTemplate: ReviewsGridCellTemplate;
  showNavigateColumn?: boolean;
}): ReviewsGridColumn[] => [
  ...(showNavigateColumn
    ? [
        {
          field: "id",
          id: "id",
          name: "",
          width: 50,
          formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
            navigateTemplate(value),
        },
      ]
    : []),
  {
    field: "serialId",
    id: "serialId",
    name: t("Id"),
    minWidth: 100,
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      reviewSerialIdTemplate(value),
    _valueGetter: (params: ReviewsGridRow) => params.serialId,
    _excelValueGetter: (row: ReviewsGridRow) => `RE - ${row.serialId}`,
  },
  {
    field: "name",
    id: "name",
    name: t("Name"),
    minWidth: 130,
    filterType: "Text",
  },
  {
    field: "description",
    id: "description",
    name: t("Description"),
    minWidth: 130,
    filterType: "Text",
    visible: false,
  },
  {
    field: "reviewFlow",
    id: "reviewFlow",
    name: t("Review Flow"),
    minWidth: 120,
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      reviewFlowTemplate(value),
    _valueGetter: (params: ReviewsGridRow) => params.reviewFlow?.name,
    _excelValueGetter: (row: ReviewsGridRow) => row.reviewFlow?.name || "-",
  },
  {
    field: "spaceName",
    id: "spaceName",
    minWidth: 130,
    name: t("Space"),
  },
  {
    field: "nextActionDueDate",
    id: "nextActionDueDate",
    name: t("Next Action Due"),
    minWidth: 150,
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      nextActionDueDateTemplate(value),
    filterType: "Date",
    _valueGetter: (params: ReviewsGridRow) => convertDate(Number(params.nextActionDueDate), "dateForTooltip"),
    _excelValueGetter: (row: ReviewsGridRow) =>
      row.nextActionDueDate ? convertDate(Number(row.nextActionDueDate), "dateForTooltip") : "-",
  },
  {
    field: "currentReviewLevel",
    id: "currentReviewLevel",
    name: t("Waiting For"),
    minWidth: 90,
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      waitingForTemplate(value),
    _valueGetter: (params: ReviewsGridRow) =>
      params.currentReviewLevel?.serial != null
        ? `${params.currentReviewLevel.serial} - ${params.currentReviewLevel.title}`
        : "-",
    _excelValueGetter: (row: ReviewsGridRow) =>
      row.currentReviewLevel?.serial != null
        ? `${row.currentReviewLevel.serial} - ${row.currentReviewLevel.title}`
        : "-",
  },
  {
    field: "template",
    id: "template",
    name: t("Project Name"),
    minWidth: 130,
    filterType: "Text",
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      projectNameTemplate(value),
    _valueGetter: (params: ReviewsGridRow) => params.projectData?.description,
    _excelValueGetter: (row: ReviewsGridRow) => row.projectData?.description || "-",
  },
  {
    field: "finishedDate",
    id: "finishedDate",
    name: t("Finished On"),
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      finishedDateTemplate(value),
    filterType: "Date",
    visible: false,
    minWidth: 130,
    _valueGetter: (params: ReviewsGridRow) => params.finishedDate,
    _excelValueGetter: (row: ReviewsGridRow) =>
      row.finishedDate ? convertDate(Number(row.finishedDate), "dateForTooltip") : "-",
  },
  {
    field: "numberOfEntitiesAttached",
    id: "numberOfEntitiesAttached",
    name: t("Contents"),
    minWidth: 60,
    visible: false,
    _valueGetter: (params: ReviewsGridRow) => params.numberOfEntitiesAttached,
  },
  {
    field: "reviewStatus",
    id: "reviewStatus",
    name: t("Stage"),
    isReviewColumnType: true,
    minWidth: 130,
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      reviewStageTemplate(value),
    filterType: "Text",
    visible: false,
    _valueGetter: (params: ReviewsGridRow) => params.reviewStatus,
    _excelValueGetter: (row: ReviewsGridRow) => {
      const { reviewStatus } = row;
      return reviewStatus && isReviewStage(reviewStatus) ? reviewStageMapper[reviewStatus].name : (reviewStatus ?? "-");
    },
  },
  {
    field: "createdBy",
    id: "createdBy",
    name: t("Created by"),
    minWidth: 180,
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) => creatorTemplate(value),
    filterType: "Text",
    visible: false,
    _valueGetter: (params: ReviewsGridRow) =>
      `${params.createdBy?.firstName ?? ""} ${params.createdBy?.lastName ?? ""}`.trim(),
    _excelValueGetter: (row: ReviewsGridRow) =>
      `${row.createdBy?.firstName ?? ""} ${row.createdBy?.lastName ?? ""}`.trim() || "-",
  },
  {
    field: "createdDate",
    id: "createdDate",
    name: t("Created On"),
    minWidth: 150,
    formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewsGridRow) =>
      createdDateTemplate(value),
    filterType: "Date",
    visible: false,
    _valueGetter: (params: ReviewsGridRow) => convertDate(Number(params.createdDate), "dateForTooltip"),
    _excelValueGetter: (row: ReviewsGridRow) =>
      row.createdDate ? convertDate(Number(row.createdDate), "dateForTooltip") : "-",
  },
];

export const getSelectProjectsGridColumns = ({
  projectImgDescTemplate,
}: {
  projectImgDescTemplate: (row: ReviewSelectProjectRow) => unknown;
}): ValidatedColumnDefinition<ReviewSelectProjectRow>[] =>
  typedColumns<ReviewSelectProjectRow>([
    {
      field: "projectId",
      id: "ProjectId",
      minWidth: 150,
      formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewSelectProjectRow) =>
        projectImgDescTemplate(value),
      name: t("ID"),
      filterType: "Text",
      wrapText: true,
    },
    {
      field: "description",
      id: "description",
      minWidth: 150,
      name: t("Name"),
      filterType: "Text",
      wrapText: true,
    },
  ]);

export const getSelectReviewsFlowsGridColumns = ({
  createdDateReviewFlowTemplate,
  createdByReviewFlowTemplate,
}: {
  createdDateReviewFlowTemplate: (row: ReviewSelectReviewFlowRow) => unknown;
  createdByReviewFlowTemplate: (row: ReviewSelectReviewFlowRow) => unknown;
}): ValidatedColumnDefinition<ReviewSelectReviewFlowRow>[] =>
  typedColumns<ReviewSelectReviewFlowRow>([
    {
      field: "name",
      id: "name",
      name: t("Name"),
      filterType: "Text",
      wrapText: true,
    },
    {
      field: "description",
      id: "description",
      minWidth: 150,
      name: t("Description"),
      filterType: "Text",
      wrapText: true,
    },
    {
      field: "createdBy",
      id: "createdBy",
      name: t("Created By"),
      formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewSelectReviewFlowRow) =>
        createdByReviewFlowTemplate(value),
    },
    {
      field: "createdDate",
      id: "createdDate",
      name: t("Created On"),
      formatter: (data: number, params: number, a: unknown, b: unknown, value: ReviewSelectReviewFlowRow) =>
        createdDateReviewFlowTemplate(value),
      filterType: "Date",
    },
  ]);

export const getFilesInReviewToolbar = ({
  addBtnTemplate,
  updateBtnTemplate,
  removeBtnTemplate,
  fileActivityBtnTemplate,
  bulkTransitionBtnTemplate,
  addSignatureToFileBtnTemplate,
  excelExportReviews,
  downloadReviewsFiles,
  allowSendTransmittals,
}: {
  addBtnTemplate: ToolbarTemplate;
  updateBtnTemplate: ToolbarTemplate;
  removeBtnTemplate: ToolbarTemplate;
  fileActivityBtnTemplate: ToolbarTemplate;
  bulkTransitionBtnTemplate: ToolbarTemplate;
  addSignatureToFileBtnTemplate: ToolbarTemplate;
  excelExportReviews: ToolbarTemplate;
  downloadReviewsFiles: ToolbarTemplate;
  allowSendTransmittals?: boolean;
}): ToolbarItem[] => {
  const list: ToolbarItem[] = [
    { id: "add_file", template: addBtnTemplate },
    { id: "update_file", template: updateBtnTemplate },
    { id: "remove_file", template: removeBtnTemplate },
    { id: "file_activity", template: fileActivityBtnTemplate },
    { id: "add_signature", template: addSignatureToFileBtnTemplate },
    { id: "download_reviews_files", template: downloadReviewsFiles },
    { id: "bulk_transition", template: bulkTransitionBtnTemplate },
  ];
  if (allowSendTransmittals) {
    list.push({
      id: "excel_export_reviews",
      template: excelExportReviews,
    });
  }

  return list;
};

export const getIssuesInReviewToolbar = ({
  addBtnTemplate,
  removeBtnTemplate,
  // exportBtnTemplate,
  exportPDFBtnTemplate,
}: {
  addBtnTemplate: ToolbarTemplate;
  removeBtnTemplate: ToolbarTemplate;
  exportBtnTemplate?: ToolbarTemplate;
  exportPDFBtnTemplate: ToolbarTemplate;
}): ToolbarItem[] => {
  const list: ToolbarItem[] = [
    { id: "add_issue", template: addBtnTemplate },
    { id: "remove_issue", template: removeBtnTemplate },
    // { id: "excel_export", template: exportBtnTemplate },
    { id: "pdf_export", template: exportPDFBtnTemplate },
  ];

  return list;
};

export const getFilesInReviewGridColumns = ({
  openFileInViewerTemplate,
  fileStatusTemplate,
  fileSizeTemplate,
  areIssuesPresentTemplate,
  fileVersionTemplate,
  fileTypeTemplate,
}: {
  openFileInViewerTemplate: AnyGridTemplate;
  fileStatusTemplate: AnyGridTemplate;
  fileSizeTemplate: AnyGridTemplate;
  areIssuesPresentTemplate: AnyGridTemplate;
  fileVersionTemplate: AnyGridTemplate;
  fileTypeTemplate: AnyGridTemplate;
}): LegacyGridColumn[] => [
  {
    type: "checkbox",
    width: 40,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileVersionId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: openFileInViewerTemplate,
    editTemplate: openFileInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: areIssuesPresentTemplate,
    editTemplate: areIssuesPresentTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "resourceType",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "details.name",
    headerText: "Name",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileType",
    headerText: "Type",
    template: fileTypeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "versionNumber",
    headerText: "Version",
    template: fileVersionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "size",
    headerText: "Size",
    template: fileSizeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "status",
    headerText: "Status",
    template: fileStatusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getIssuesInReviewGridColumns = ({
  openIssueInViewerTemplate,
  officialResponseTemplate,
  severityTemplate,
  issueImageTemplate,
  fileVersionTemplate,
  issueStatusTemplate,
  issueTitleTemplate,
  fileNameTemplate,
}: {
  openIssueInViewerTemplate: AnyGridTemplate;
  officialResponseTemplate: AnyGridTemplate;
  severityTemplate: AnyGridTemplate;
  issueImageTemplate: AnyGridTemplate;
  fileVersionTemplate: AnyGridTemplate;
  issueStatusTemplate: AnyGridTemplate;
  issueTitleTemplate: AnyGridTemplate;
  fileNameTemplate: AnyGridTemplate;
}): LegacyGridColumn[] => [
  {
    type: "checkbox",
    width: 50,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "issueId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "resourceType",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: openIssueInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "snapshot",
    width: "100",
    headerText: "Image",
    textAlign: "Center",
    template: issueImageTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "title",
    headerText: "Title",
    template: issueTitleTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "officialResponse",
    headerText: "Official Response",
    template: officialResponseTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "severity",
    headerText: "Severity",
    template: severityTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "file",
    headerText: "File",
    template: fileNameTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileVersionNumber",
    headerText: "File Version",
    template: fileVersionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "status",
    headerText: "Status",
    template: issueStatusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getResourcesActivityColumns = ({
  openFileInViewerTemplate,
  fileNameTemplate,
  fileSizeTemplate,
  versionTemplate,
  creatorTemplate,
  dateTemplate,
  statusTemplate,
  modificationTypeTemplate,
}: {
  openFileInViewerTemplate: AnyGridTemplate;
  fileNameTemplate: AnyGridTemplate;
  fileSizeTemplate: AnyGridTemplate;
  versionTemplate: AnyGridTemplate;
  creatorTemplate: AnyGridTemplate;
  dateTemplate: AnyGridTemplate;
  statusTemplate: AnyGridTemplate;
  modificationTypeTemplate: AnyGridTemplate;
}): LegacyGridColumn[] => [
  {
    field: "fileVersionId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    headerText: "",
    width: "30",
    textAlign: "Center",
    template: openFileInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileName",
    headerText: "Name",
    template: fileNameTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileSize",
    headerText: "File Size",
    template: fileSizeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "versionNumber",
    headerText: "Version",
    template: versionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "updatedBy",
    headerText: "Modified by",
    template: creatorTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "updatedDate",
    headerText: "Modified Date",
    template: dateTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "status",
    headerText: "Last Status",
    template: statusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "type",
    headerText: "Modification Type",
    template: modificationTypeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

/**
 * KNOWN BUG, preserved verbatim: `statusCategories.find(...)` returns undefined for any
 * category not in the portal catalogue (e.g. a status whose category was renamed), and the
 * destructure below then throws "Cannot destructure property 'color' of undefined". The `!`
 * is erased — the crash is intentionally left in place.
 */
export const getStatusBtnStyle = (category: unknown): { backgroundColor: string; color: string } => {
  const matchedCategory = statusCategories.find((record) => record.name === category)!;
  const { color, backgroundHexcode: backgroundColor } = matchedCategory;

  const style = { backgroundColor, color };
  return style;
};

/**
 * KNOWN BUG, preserved verbatim: same unguarded `find` as {@link getStatusBtnStyle} — an
 * unknown category throws "Cannot read properties of undefined (reading 'background')".
 */
export const getDropdownBackground = (category: unknown): string => {
  const matchedCategory = statusCategories.find((record) => record.name === category)!;
  const background = matchedCategory.background;
  return background;
};
